import { CustomClaimValue } from "@app/shared";
import cn from "classnames";
import { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "../shared";
import { AuthorizeView } from "../shared/AuthorizeView";
import { ErrorBoundaryFallback } from "@app/shared/src/components/ErrorBoundaryFallback";
import { DashboardLayout, DashboardLayoutProps } from "./DashboardLayout";

type TabBarTab = {
    title: string | ReactNode;
    child: ReactNode;
    requiredClaim?: CustomClaimValue
}

type TabbedDashLayoutProps = {
    tabs: TabBarTab[];
} & Omit<DashboardLayoutProps, "children">;


export const TabbedDashLayout: React.FC<TabbedDashLayoutProps> = ({ tabs, ...props }) => {
    const navigate = useNavigate();
    const params = useSearchParams();
    // `?tab=` accepts either a positional index or a tab title ("Upgrade"). Titles survive
    // changes to the tab array's shape (a tab inserted in the middle, conditional or
    // claim-gated tabs), so links from elsewhere in the app should prefer them.
    const requestedTab = params[0].get("tab") ?? "0";
    const titleIndex = tabs.findIndex((t) => typeof t.title === "string" && t.title.toLowerCase() === requestedTab.toLowerCase());
    const requestedIndex = titleIndex >= 0 ? titleIndex : parseInt(requestedTab);
    const tab = Number.isNaN(requestedIndex) || requestedIndex < 0 || requestedIndex >= tabs.length ? 0 : requestedIndex;
    const innerShadow = { boxShadow: "inset 0px -4px 4px -4px rgba(0, 0, 0, .1)" };

    return (
        <DashboardLayout {...props} bodyClassName="">
            {/* Below md the strip wraps onto extra rows: eight tabs at phone width otherwise clip
                at the edge with nothing to say more exist, and a sideways scroll is the one thing a
                phone layout must not need. From md up it scrolls within itself as before, so a long
                strip never forces the page wider than the viewport. */}
            <div className="flex flex-wrap md:flex-nowrap md:overflow-x-auto">
                {tabs.map((t, i) => {
                    const TabHtml = (
                        <div key={t.requiredClaim ? undefined : i} style={i === tab ? {} : innerShadow} className={cn("border-r border-separators-page py-2 px-4 md:px-6 shrink-0 whitespace-nowrap", { "border-b bg-background-top-nav-tabs": i !== tab })}>
                            <Link onClick={() => {
                                var newParams = new URLSearchParams(params[0]);
                                newParams.set("tab", i.toString());
                                navigate(`?${newParams.toString()}`);
                            }}>{t.title}</Link>
                        </div>
                    );
                    return t.requiredClaim ? (<AuthorizeView key={i} claim={t.requiredClaim} children={TabHtml}></AuthorizeView>) : TabHtml;
                })}
                <div style={innerShadow} className="border-b flex-grow border-separators-page bg-background-top-nav-tabs"></div>
            </div>
            <div className="p-3">
                <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
                    {tabs[tab].child}
                </ErrorBoundary>
            </div>
        </DashboardLayout>
    );
}